import { Avatar, Button, Flex, Modal, ScrollArea } from "@mantine/core";
import styled from "styled-components";
import { AssignmentConflict } from "../../../api/shiftDefinitionApi";
import { formatDayKey } from "../../../utils/shiftPattern";

/**
 * Atama çakışması (plan 2.24): aynı kişiye aynı tarihte iki vardiya atanamaz —
 * o günün puantaj ölçütünün tek cevabı olmalı.
 *
 * Çakışmaların HEPSİ "kapatılabilir" ise bu, dönüşümlü vardiyanın normal
 * hâlidir (kişinin önceki düzeni sürüyor, yenisi devralacak) ve modal ikinci
 * bir buton sunar: önceki düzeni yeni atamanın bir gün öncesinde kapat.
 * Aksi hâlde gerçek bir çakışma vardır ve sessizce düzeltilmez.
 */
type Props = {
  opened: boolean;
  setOpened: (value: boolean) => void;
  conflicts: AssignmentConflict[];
  /** Verilirse ve tüm çakışmalar kapatılabilirse "devam et" butonu çıkar. */
  onCloseAndContinue?: () => void;
};

const ShiftConflictErrorModal = ({
  opened,
  setOpened,
  conflicts,
  onCloseAndContinue,
}: Props) => {
  const allCloseable =
    conflicts.length > 0 && conflicts.every((c) => c.closeable);
  const canContinue = allCloseable && Boolean(onCloseAndContinue);

  return (
    <Modal
      size={560}
      radius="20px"
      opened={opened}
      centered
      onClose={() => setOpened(false)}
    >
      <ModalContainer>
        <IconWrapper $tone={canContinue ? "warn" : "error"}>
          <Avatar src="/assets/redInfoCircle.png" size={48} radius={0} />
        </IconWrapper>

        <Flex direction="column" gap="12px">
          <ModalTitle>Vardiya Çakışması</ModalTitle>
          <ModalText>
            {canContinue
              ? "Aşağıdaki çalışanların bu tarihlerde süren bir düzeni var. " +
                "Devam ederseniz eski düzenleri yeni atamanın bir gün öncesinde kapatılır."
              : "Aşağıdaki çalışanların çakışan vardiyaları sebebiyle işlem gerçekleştirilemedi."}
          </ModalText>
        </Flex>

        <ScrollArea h={180}>
          <Flex gap={12} direction="column">
            {conflicts.map((conflict) => (
              <Flex align="center" gap={10} key={conflict.assignmentId}>
                <Avatar size={24}>{conflict.employeeName?.[0] ?? "?"}</Avatar>
                <Flex direction="column">
                  <ConflictUserName
                    onClick={() =>
                      window.open(`/employees/${conflict.employeeId}`, "_blank")
                    }
                  >
                    {conflict.employeeName}
                  </ConflictUserName>
                  <ConflictDetail>
                    {conflict.shiftName} · {formatDayKey(conflict.startDate)} –{" "}
                    {conflict.endDate ? formatDayKey(conflict.endDate) : "süresiz"}
                    {!conflict.closeable && <Blocking> · çakışıyor</Blocking>}
                  </ConflictDetail>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </ScrollArea>

        <Flex justify="center" gap="10px">
          <CancelButton unstyled onClick={() => setOpened(false)}>
            <CancelButtonLabel>{canContinue ? "Vazgeç" : "Kapat"}</CancelButtonLabel>
          </CancelButton>
          {canContinue && (
            <ConfirmButton
              unstyled
              onClick={() => {
                setOpened(false);
                onCloseAndContinue!();
              }}
            >
              <ConfirmButtonLabel>Eskisini kapat, devam et</ConfirmButtonLabel>
            </ConfirmButton>
          )}
        </Flex>
      </ModalContainer>
    </Modal>
  );
};

export default ShiftConflictErrorModal;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 40px 20px 40px;
  gap: 28px;
`;

const IconWrapper = styled.div<{ $tone: "warn" | "error" }>`
  align-self: center;
  width: max-content;
  padding: 15px;
  border-radius: 50%;
  background-color: ${({ $tone }) => ($tone === "warn" ? "#fff4e6" : "#ffe8e9")};
`;

const CancelButton = styled(Button)<any>`
  min-width: 130px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid black;
  background-color: transparent;
  color: black;
  cursor: pointer;
  align-items: center;
  display: flex;
  justify-content: center;
`;

const CancelButtonLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  cursor: pointer;
`;

const ConfirmButton = styled(Button)<any>`
  min-width: 200px;
  height: 44px;
  border-radius: 10px;
  padding: 11px 20px;
  background-color: #5c26ff;
  cursor: pointer;
  border: none;
`;

const ConfirmButtonLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  cursor: pointer;
  color: white;
`;

const ModalTitle = styled.div`
  font-weight: 600;
  font-size: 24px;
  line-height: 28px;
  text-align: center;
  color: black;
`;

const ModalText = styled.div`
  font-weight: 400;
  font-size: 15px;
  text-align: center;
  color: #828282;
  line-height: 22px;
`;

const ConflictUserName = styled.div`
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: #3b82f6;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ConflictDetail = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const Blocking = styled.span`
  color: #e03131;
  font-weight: 500;
`;
